import { Injectable } from '@angular/core';

/**
 * The fields the quick-actions row can surface. Keys are persisted, so renaming
 * one silently drops that field from every user's stored preferences.
 */
export type QuickActionFieldKey =
  | 'company'
  | 'location'
  | 'assignee'
  | 'po_number'
  | 'quote_id'
  | 'shipping_date'
  | 'created'
  | 'order_id';

/** One row entry as consumed by the shared `ui-table-column-settings` popover. */
export interface IQuickActionField {
  key: QuickActionFieldKey;
  /** i18n key, resolved by the picker and the row. */
  label: string;
  visible: boolean;
}

/** Persisted state for the quick-actions row. */
export interface IQuickActionsUiState {
  /** Whether the strip is rendered at all. */
  enabled?: boolean;
  /** Map of field key → visible. */
  fields?: Record<string, boolean>;
  /** Ordered list of field keys (visible-first, in display order). */
  order?: string[];
  updated_at?: string;
}

interface IQuickActionsStorage {
  rows: Record<string, IQuickActionsUiState>;
}

/**
 * Single source of truth for the quick-actions row's per-user preferences
 * (row on/off, which fields, and their order). WB-7478.
 *
 * Deliberately mirrors `TablePreferencesService`: one shared `localStorage` key,
 * every read/write funnelled through this service so the storage tier can be
 * swapped for a real user-preferences endpoint later without touching a single
 * component. Keyed by `rowKey` so a second surface (order details, the inbox
 * sidebar) can keep its own layout.
 *
 * Note: `localStorage` is per-browser, not per-account. The ticket asks for the
 * state to follow the user across sessions; that holds within a browser and
 * matches how column preferences already behave repo-wide.
 */
@Injectable({ providedIn: 'root' })
export class QuickActionsPreferencesService {
  private static readonly STORAGE_KEY = 'wolsal.ui.quick-actions';

  /** Hard cap from the spec: the row must never wrap or scroll horizontally. */
  static readonly MAX_VISIBLE = 5;

  /** Raw persisted state for a single row (empty object if none/invalid). */
  getRowState(rowKey: string): IQuickActionsUiState {
    return this.read().rows[rowKey] ?? {};
  }

  /**
   * Whether the row is enabled. Defaults to `false` — the row is opt-in, so a
   * user who has never touched the toggle sees the panel exactly as before.
   */
  isEnabled(rowKey: string): boolean {
    return this.getRowState(rowKey).enabled === true;
  }

  setEnabled(rowKey: string, enabled: boolean): void {
    const storage = this.read();
    storage.rows[rowKey] = {
      ...(storage.rows[rowKey] ?? {}),
      enabled,
      updated_at: new Date().toISOString(),
    };
    this.write(storage);
  }

  /**
   * Apply persisted visibility + order onto the default field descriptors,
   * returning a fresh ordered array of clones. Unknown stored keys are dropped
   * and fields added in code later are appended in definition order, so this is
   * safe across releases.
   *
   * The `MAX_VISIBLE` cap is re-applied on read: if stored state somehow holds
   * more than five visible fields (hand-edited storage, or a future default set
   * that grew), the overflow is trimmed rather than allowed to break the layout.
   */
  applyPreferences(rowKey: string, defaults: IQuickActionField[]): IQuickActionField[] {
    const state = this.getRowState(rowKey);
    const byKey = new Map<QuickActionFieldKey, IQuickActionField>(
      defaults.map((field) => [field.key, { ...field }])
    );

    if (state.fields) {
      byKey.forEach((field, key) => {
        const saved = state.fields?.[key];
        if (saved !== undefined) {
          field.visible = !!saved;
        }
      });
    }

    const ordered = this.resolveOrder(rowKey, defaults.map((field) => field.key))
      .map((key) => byKey.get(key))
      .filter((field): field is IQuickActionField => !!field);

    let shown = 0;
    ordered.forEach((field) => {
      if (!field.visible) return;
      shown += 1;
      if (shown > QuickActionsPreferencesService.MAX_VISIBLE) {
        field.visible = false;
      }
    });

    return ordered;
  }

  /**
   * Resolve field order: saved order first (stale keys dropped), then any keys
   * missing from storage appended in their natural definition order.
   */
  resolveOrder(rowKey: string, allKeys: QuickActionFieldKey[]): QuickActionFieldKey[] {
    const saved = (this.getRowState(rowKey).order ?? []) as QuickActionFieldKey[];
    const known = new Set<QuickActionFieldKey>(allKeys);
    const ordered = saved.filter((key) => known.has(key));
    const seen = new Set(ordered);
    allKeys.forEach((key) => {
      if (!seen.has(key)) ordered.push(key);
    });
    return ordered;
  }

  /** Persist the given ordered fields' visibility and order. */
  save(rowKey: string, fields: IQuickActionField[]): void {
    const storage = this.read();
    const fieldsState: Record<string, boolean> = { ...(storage.rows[rowKey]?.fields ?? {}) };
    fields.forEach((field) => {
      fieldsState[field.key] = !!field.visible;
    });

    storage.rows[rowKey] = {
      ...(storage.rows[rowKey] ?? {}),
      fields: fieldsState,
      order: fields.map((field) => field.key),
      updated_at: new Date().toISOString(),
    };
    this.write(storage);
  }

  private read(): IQuickActionsStorage {
    try {
      const raw = localStorage.getItem(QuickActionsPreferencesService.STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object' && parsed.rows) {
        return parsed as IQuickActionsStorage;
      }
    } catch {
      // fall through to empty state on malformed JSON / disabled storage
    }
    return { rows: {} };
  }

  private write(storage: IQuickActionsStorage): void {
    try {
      localStorage.setItem(QuickActionsPreferencesService.STORAGE_KEY, JSON.stringify(storage));
    } catch {
      // ignore quota / disabled storage errors — preferences are best-effort
    }
  }
}
